export const TRACE_ARG = "_trace";

export const TRACE_LOG_PREFIX = "trace";

export const UNTRACED_MODULES: readonly string[] = ["prosemirror"];

const TRACE_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const CONVEX_REQUEST_ID_PATTERN = /\[Request ID:\s*([A-Za-z0-9_-]{1,64})\s*\]/i;
const CONVEX_FUNCTION_NAME_PATTERN = /\[CONVEX [QMA]\(([A-Za-z0-9_./:-]{1,200})\)\]/;
const CONVEX_SERVER_ERROR_PATTERN = /\[Request ID:\s*[^\]]+\]\s*Server Error/i;

export interface TraceContext {
  sessionId?: string;
  requestId?: string;
}

function traceId(value: string | undefined): string | undefined {
  return value !== undefined && TRACE_ID_PATTERN.test(value) ? value : undefined;
}

export function normalizeTraceContext(
  input: TraceContext | undefined,
): TraceContext | undefined {
  const sessionId = traceId(input?.sessionId);
  const requestId = traceId(input?.requestId);
  if (sessionId === undefined && requestId === undefined) return undefined;
  return {
    ...(sessionId !== undefined && { sessionId }),
    ...(requestId !== undefined && { requestId }),
  };
}

export function acceptsTraceContext(functionName: string): boolean {
  const separator = functionName.indexOf(":");
  const modulePath =
    separator === -1 ? functionName : functionName.slice(0, separator);
  return !UNTRACED_MODULES.includes(modulePath);
}

export function withTraceContext<Args extends object>(
  functionName: string,
  args: Args,
  context: TraceContext | undefined,
): Args {
  const trace = normalizeTraceContext(context);
  if (trace === undefined || !acceptsTraceContext(functionName)) return args;
  return { ...args, [TRACE_ARG]: trace };
}

export function traceLogLine(context: TraceContext | undefined): string | null {
  const trace = normalizeTraceContext(context);
  if (trace === undefined) return null;
  const parts = [TRACE_LOG_PREFIX];
  if (trace.sessionId !== undefined) parts.push(`session=${trace.sessionId}`);
  if (trace.requestId !== undefined) parts.push(`request=${trace.requestId}`);
  return parts.join(" ");
}

export function extractConvexRequestId(text: string): string | undefined {
  return CONVEX_REQUEST_ID_PATTERN.exec(text)?.[1];
}

export function extractConvexFunctionName(text: string): string | undefined {
  return CONVEX_FUNCTION_NAME_PATTERN.exec(text)?.[1];
}

export function isRedactedConvexServerError(text: string): boolean {
  return CONVEX_SERVER_ERROR_PATTERN.test(text);
}

export function convexRequestIdOf(error: unknown): string | undefined {
  let current = error;
  for (let depth = 0; depth < 5 && current instanceof Error; depth += 1) {
    const requestId = extractConvexRequestId(current.message);
    if (requestId !== undefined) return requestId;
    current = Reflect.get(current, "cause");
  }
  return typeof error === "string" ? extractConvexRequestId(error) : undefined;
}
